import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { PublicNoticeMutations } from '@api/services/public-notices';
import LoaderButton from '@components/loader/LoaderButton';
import { Table as UswdsTable, Tooltip as UswdsTooltip } from '@trussworks/react-uswds';
import { mdiChat, mdiEye, mdiFileExport } from '@mdi/js';
import dayjs from '@src/utils/dayjs';

import Table from '@components/table/Table';
import Icon from '@components/icon/Icon';

import ActionsTableCell from './ActionsTableCell';

import './publicNoticesTable.scss';
import { useMemo } from 'react';
import { usePublicNotices, useTableStore } from '@src/stores';
import { buildPublicNoticesSearch } from '../publicNoticeHelpers';

export const publicNoticesTableId = 'public-notices-table';

const typeHelp = [
  { code: 'DEVINLIEUA', name: 'Develop In-Lieu Fee Program' },
  { code: 'DEVMBA', name: 'Develop Mitigation Bank' },
  { code: 'DEVRPSS', name: 'Develop RGP/PGP/SPGP/Section 404-LOP' },
  { code: 'DWOFA', name: 'Develop 214 WRDA/Other Funding Agreements' },
  { code: 'FAEM', name: 'Funding Agreement Extension/Modification' },
  { code: 'LOP', name: 'Letter of Permission' },
  { code: 'MOD', name: 'Instrument Modification' },
  { code: 'NWP', name: 'Nationwide Permit' },
  { code: 'PGP', name: 'Programmatic General Permit' },
  { code: 'RGP', name: 'Regional General Permit' },
  { code: 'SP', name: 'Standard Permit' },
  { code: 'SPN', name: 'Special Public Notice' },
];

const actionsTooltipContent = (
  <UswdsTable compact bordered fullWidth className='margin-top-0 padding-0'>
    <thead>
      <tr>
        <th scope='col'>Icon</th>
        <th scope='col'>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td className='center'>
          <Icon focusable={false} path={mdiEye} size={'16px'} />
        </td>
        <td>View Full Public Notice</td>
      </tr>
      <tr>
        <td className='center'>
          <Icon focusable={false} path={mdiChat} size={'16px'} />
        </td>
        <td>Submit Comments</td>
      </tr>
    </tbody>
  </UswdsTable>
);

const typeTooltipContent = (
  <UswdsTable compact bordered fullWidth className='margin-top-0 padding-0'>
    <thead>
      <tr>
        <th scope='col'>Short Code</th>
        <th scope='col'>Name</th>
      </tr>
    </thead>
    <tbody>
      {typeHelp.map((type) => (
        <tr key={type.code}>
          <td>{type.code}</td>
          <td>{type.name}</td>
        </tr>
      ))}
    </tbody>
  </UswdsTable>
);

const iconProps = {
  focusable: false,
  size: 1,
};

const columnOrder = [
  { name: 'actions', alignment: 'center' },
  { name: 'daNumber' },
  { name: 'projectName' },
  { name: 'actionType' },
  { name: 'startDate' },
  { name: 'commentsEndDate' },
  { name: 'applicantName' },
];

const headerData = [
  [
    {
      label: 'Actions',
      name: 'actions',
      helpText: actionsTooltipContent,
      isSortable: false,
      width: '5.5rem',
    },
    {
      label: 'DA Number',
      name: 'daNumber',
    },
    {
      label: 'Project Name',
      name: 'projectName',
      helpText: 'Hover over the project names to view a description of each project.',
    },
    {
      label: 'Type',
      name: 'actionType',
      helpText: typeTooltipContent,
    },
    {
      label: 'Date of Public Notice',
      name: 'startDate',
    },
    {
      label: 'Date Comment Period Ends',
      name: 'commentsEndDate',
    },
    {
      label: 'Applicant',
      name: 'applicantName',
      isSortable: false,
    },
  ],
];

const projectNameWithTooltip = ({ projectName, description }) => {
  return description ? (
    <UswdsTooltip
      className='project-description-tooltip'
      position='right'
      label={description}
      onMouseDown={(e) => e.preventDefault()}
    >
      <span>{projectName}</span>
    </UswdsTooltip>
  ) : (
    <UswdsTooltip
      className='project-description-tooltip'
      position='right'
      label={'No Description Available'}
      onMouseDown={(e) => e.preventDefault()}
    >
      <span>{projectName}</span>
    </UswdsTooltip>
  );
};

const PublicNoticesTable = ({ publicNotices }) => {
  const navigate = useNavigate();
  const {
    getPageNumber,
    getRecordCount,
    getSortColumn,
    getSortDirection,
    setPageNumber,
    setPageSize,
    setSortColumn,
    setSortDirection,
  } = useTableStore();
  const { districtCodes, searchText, stateCodes } = usePublicNotices();

  const recordCount = getRecordCount(publicNoticesTableId);
  const pageNumber = getPageNumber(publicNoticesTableId);
  const sortColumn = getSortColumn(publicNoticesTableId);
  const sortOrder = getSortDirection(publicNoticesTableId);

  const { mutate: exportAllAsCsv, isPending: isLoadingCsv } = useMutation({
    mutationFn: PublicNoticeMutations.exportAllAsCsv,
    onSuccess: (data) => {
      const { fileContents, fileDownloadName } = data;
      const decodedContent = atob(fileContents);
      const blob = new Blob([decodedContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = fileDownloadName || 'PublicNotices.csv';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  });

  const updatePublicNoticesUrl = ({ nextPageNumber = pageNumber }) => {
    navigate({
      to: '/public-notices',
      search: () =>
        buildPublicNoticesSearch({
          districtCodes,
          stateCodes,
          pageNumber: nextPageNumber,
          searchText,
        }),
      replace: true,
    });
  };

  const handlePageChange = (nextPageNumber) => {
    setPageNumber(publicNoticesTableId, nextPageNumber);
    updatePublicNoticesUrl({ nextPageNumber });
  };

  const handlePageSizeChange = (nextItemsPerPage) => {
    setPageSize(publicNoticesTableId, nextItemsPerPage);
    setPageNumber(publicNoticesTableId, 1);
    updatePublicNoticesUrl({ nextPageNumber: 1 });
  };

  const handleSortDirectionChange = (nextSortDirection) => {
    setSortDirection(publicNoticesTableId, nextSortDirection);
  };

  const handleSortNameChange = (nextSortColumn) => {
    setSortColumn(publicNoticesTableId, nextSortColumn);
  };

  const rowData = useMemo(
    () =>
      publicNotices.map((publicNotice) => ({
        id: publicNotice.publicNoticeID,
        actions: <ActionsTableCell publicNotice={publicNotice} />,
        daNumber: publicNotice.daNumber,
        projectName: projectNameWithTooltip({
          projectName: publicNotice.projectName,
          description: publicNotice.description,
        }),
        actionType: publicNotice.actionType,
        startDate: dayjs.utc(publicNotice.startDate).format('YYYY-MM-DD'),
        commentsEndDate: dayjs.utc(publicNotice.commentsEndDate).format('YYYY-MM-DD'),
        applicantName: publicNotice.applicantCompany || publicNotice.applicantName,
      })),
    [publicNotices]
  );

  const handleCsvExport = () => {
    exportAllAsCsv({
      districtCodes,
      searchText,
      sortColumn,
      sortOrder,
      states: stateCodes,
    });
  };
  return (
    <Table
      columnOrder={columnOrder}
      headerData={headerData}
      id={publicNoticesTableId}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      onSortNameChange={handleSortNameChange}
      onSortDirectionChange={handleSortDirectionChange}
      rightContent={
        <LoaderButton
          className='action-button button-small'
          isLoading={isLoadingCsv}
          onClick={handleCsvExport}
          title='Export CSV'
          type='button'
        >
          <Icon path={mdiFileExport} {...iconProps} />
          Export CSV
        </LoaderButton>
      }
      rowData={rowData}
      secondarySort='createdDate'
      totalRecords={recordCount}
    />
  );
};

export default PublicNoticesTable;
